import { NgModule, Injector, DoBootstrap } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CreatorCoreModule } from './creator-core/creator-core.module';
import { AppRouting } from './app-routing';
import { DynamicFormModule } from './pages/component-forms/custom/custom-form-parts/dynamic-form/dynamic-form.module';
import { AppComponent } from './app.component';
import { ComponentFormsDirective } from './directives/component-forms.directive';
import { DragDirective } from './directives/drag-and-resize/drag.directive';
import { ResizeBottomDirective } from './directives/drag-and-resize/resize-bottom.directive';
import { ResizeLeftDirective } from './directives/drag-and-resize/resize-left.directive';
import { ResizeRightDirective } from './directives/drag-and-resize/resize-right.directive';
import { ResizeTopDirective } from './directives/drag-and-resize/resize-top.directive';
import { GooglePlacesDirective } from './directives/google-places.directive';
import { JvectorMapDirective } from './directives/jvector-map.directive';
import { AdminGuard } from './guards/admin.guard';
import { AuthGuard } from './guards/auth.guard';
import { EditUserFormComponent } from './pages/admin/users/user-list/edit-user-form/edit-user-form.component';
import { DeleteUserFormComponent } from './pages/admin/users/user-list/delete-user-form/delete-user-form.component';
import { UserListItemComponent } from './pages/admin/users/user-list/user-list-item/user-list-item.component';
import { UserListComponent } from './pages/admin/users/user-list/user-list.component';
import { LoginComponent } from './pages/auth/login/login.component';
import { MaintenanceComponent } from './pages/maintenance/maintenance.component';
import { MaintenanceGuard } from './guards/maintenance.guard';
import { MaintenanceService } from './services/maintenance.service';
import {
	AddToCalendarComponent,
	CarouselComponent,
	CustomComponent,
	DefaultComponent,
	DeviceTargetingComponent,
	DynamicCTAComponent,
	EngagementTrackerComponent,
	FadeComponent,
	GeoTargetedImagesComponent,
	SegmentedImagesComponent,
	ImageSwapComponent,
	InstagramComponent,
	MapComponent,
	NewsTickerComponent,
	PersonalizedImageComponent,
	PersonalizedImageModalComponent,
	PopupComponent,
	LocationComponent,
	TimerComponent,
	TwitterComponent,
	VideoComponent,
	WeatherComponent,
	WebScrapeComponent,
	WebScrapeModalComponent,
	WhatsappComponent,
	ZoomComponent,
	RevealComponent,
	AssetGeneratorComponent,
	GalleryComponent,
	RuleEngineComponent,
	RecommendationComponent,
	RecommendationModalComponent,
	CouponComponent,
	SurveyComponent,
	BarcodeComponent,
	TranslateComponent,
	ProgressBarComponent
} from './pages/component-forms/index';
import { ComponentListComponent } from './pages/component-list/component-list.component';
import { ComponentListItemComponent } from './pages/component-list/component-list-item/component-list-item.component';
import { ComponentReportComponent } from './pages/component-report/component-report.component';
import { ComponentSnippetComponent } from './pages/component-snippet/component-snippet.component';
import { ComponentFormsContainerComponent } from './pages/component-forms-container/component-forms-container.component';
import { FileEditModalComponent } from './pages/file-manager/file-edit-modal/file-edit-modal.component';
import { FileGridViewComponent } from './pages/file-manager/file-grid-view/file-grid-view.component';
import { FileListViewComponent } from './pages/file-manager/file-list-view/file-list-view.component';
import { FileManagerComponent } from './pages/file-manager/file-manager.component';
import { NewComponentItemComponent } from './pages/component-new/component-item/component-item.component';
import { ComponentNewComponent } from './pages/component-new/component-new.component';
import { FileFieldComponent } from './pages/page-parts/file-field/file-field.component';
import { NameFieldComponent } from './pages/page-parts/name-field/name-field.component';
import { OverlayComponent } from './pages/page-parts/overlay/overlay.component';
import { PreviewComponent } from './pages/page-parts/preview/preview.component';
import { EmailClientSupportComponent } from './pages/page-parts/preview/email-client-support/email-client-support.component';
import { ClientSelectedPreviewsComponent } from './pages/page-parts/preview/client-selected-previews/client-selected-previews.component';
import { TopMenuComponent } from './pages/page-parts/top-menu/top-menu.component';
import { HowtoComponent } from './pages/page-parts/how-to/howto.component';
import { AuthService } from './services/auth.service';
import { ComponentService } from './services/component.service';
import { NewApiHttpService } from './services/new-api-http.service';
import { FilesManagerService } from './services/files-manager.service';
import { SmartLocalStorageService } from './services/smart-local-storage.service';
import { TimerModalComponent } from './pages/component-forms/timer/timer-modal/timer-modal.component';
import { InlineEditorComponent } from './pages/component-forms/timer/timer-modal/inline-editor/inline-editor.component';
import { ComponentPageContainerComponent } from './pages/component-page-container/component-page-container.component';
import { TimerFormatsService } from './services/timer-formats.service';
import { DateTimeService } from './services/date-time.service';
import { ChartsService } from './services/charts.service';
import { GalleryImageComponent } from './pages/component-forms/gallery/gallery-image/gallery-image.component';
import { AssetSettingComponent } from './pages/component-forms/asset-generator/asset-setting/asset-setting.component';
import { TimezoneService } from './services/timezone.service';
import { FontsService } from './services/fonts.service';
import { OverlayService } from './services/overlay.service';
import { PIInlineEditorComponent } from './pages/component-forms/personalized-image/personalized-image-modal/inline-editor/pi-inline-editor.component';
import { BeeFreeService } from './services/beefree.service';
import { ButtonsPreviewGenerateComponent } from './pages/page-parts/buttons-preview-generate/buttons-preview-generate.component';
import { GifCompressionFieldComponent } from './pages/page-parts/gif-compression-field/gif-compression-field.component';
import { createCustomElement } from '@angular/elements';
import { PageWidthService } from './services/page-width.service';
import { EmbeddedFrameService } from './services/embedded-frame.service';
import { ReturnToWidgetLibraryComponent } from './pages/page-parts/return-to-widget-library/returntowidgetlibrary.component';

@NgModule({
	declarations: [
		AppComponent,
		LoginComponent,
		ComponentNewComponent,
		TopMenuComponent,
		HowtoComponent,
		NewComponentItemComponent,
		ComponentFormsContainerComponent,
		ComponentFormsDirective,
		TimerComponent,
		PreviewComponent,
		EmailClientSupportComponent,
		ClientSelectedPreviewsComponent,
		FileManagerComponent,
		ComponentSnippetComponent,
		ComponentListComponent,
		ComponentListItemComponent,
		OverlayComponent,
		ComponentReportComponent,
		JvectorMapDirective,
		FileGridViewComponent,
		FileListViewComponent,
		FileFieldComponent,
		NameFieldComponent,
		AddToCalendarComponent,
		ZoomComponent,
		FadeComponent,
		DynamicCTAComponent,
		EngagementTrackerComponent,
		WhatsappComponent,
		WeatherComponent,
		GooglePlacesDirective,
		MapComponent,
		LocationComponent,
		VideoComponent,
		DeviceTargetingComponent,
		DefaultComponent,
		WebScrapeComponent,
		WebScrapeModalComponent,
		DragDirective,
		ResizeTopDirective,
		ResizeBottomDirective,
		ResizeLeftDirective,
		ResizeRightDirective,
		TwitterComponent,
		InstagramComponent,
		PersonalizedImageComponent,
		PersonalizedImageModalComponent,
		PopupComponent,
		CarouselComponent,
		NewsTickerComponent,
		ImageSwapComponent,
		UserListComponent,
		UserListItemComponent,
		EditUserFormComponent,
		DeleteUserFormComponent,
		GeoTargetedImagesComponent,
		SegmentedImagesComponent,
		CustomComponent,
		FileEditModalComponent,
		TimerModalComponent,
		InlineEditorComponent,
		ComponentPageContainerComponent,
		RevealComponent,
		AssetGeneratorComponent,
		GalleryComponent,
		GalleryImageComponent,
		AssetSettingComponent,
		RuleEngineComponent,
		RecommendationComponent,
		RecommendationModalComponent,
		CouponComponent,
		SurveyComponent,
		PIInlineEditorComponent,
		BarcodeComponent,
		TranslateComponent,
		ProgressBarComponent,
		ButtonsPreviewGenerateComponent,
		GifCompressionFieldComponent,
		MaintenanceComponent,
		ReturnToWidgetLibraryComponent
	],
	imports: [BrowserModule, BrowserAnimationsModule, AppRouting, CreatorCoreModule, DynamicFormModule],
	providers: [
		AuthGuard,
		SmartLocalStorageService,
		ComponentService,
		NewApiHttpService,
		AuthService,
		FilesManagerService,
		AdminGuard,
		TimerFormatsService,
		DateTimeService,
		ChartsService,
		TimezoneService,
		FontsService,
		OverlayService,
		BeeFreeService,
		PageWidthService,
		EmbeddedFrameService,
		BeeFreeService,
		MaintenanceGuard,
		MaintenanceService
	],
	entryComponents: [AppComponent]
})
export class AppModule implements DoBootstrap {
	constructor(private injector: Injector) {
		const el = createCustomElement(AppComponent, { injector });
		customElements.define('dynamicmail-creator', el);
	}
	// eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
	ngDoBootstrap() {}
}
